import { Graph, HashableNodeId, NodeId, nodeIdToHashable, V2Data } from 'dashboard/models/graph';
import { Backend } from 'dashboard/models/graph-backend';
import { GraphExplorer } from 'dashboard/models/graph-explorer';
import {
  convertHydratedNodeToLightlyHydratedNode,
  convertToHydratedNode,
  convertToLightlyHydratedNode,
  HydratedNode,
  LightlyHydratedNode,
} from 'dashboard/models/graph-hydration';
import {
  GraphQuery,
  graphQueryResponseToV2Data,
  NHopsFromAccountQuery,
  QueryType,
  sanitizeGraphQuery,
  TextValue,
} from 'dashboard/models/graph-query';
import { GraphSnapshot, snapshotNodeIdToGraphNodeId } from 'dashboard/models/graph-snapshot';
import { ACCOUNT_DATUM_TYPE } from 'lib/constants/graph';

export interface GraphQueryInfo {
  graphQuery: GraphQuery;
  hydrationData: Map<HashableNodeId, HydratedNode>;
  lightHydrationData: Map<HashableNodeId, LightlyHydratedNode>;
  v2Data: V2Data;
}

export interface GraphQueryInfoContext {
  backend: Backend;
  graph: Graph;
  graphExplorer: GraphExplorer;
}

function accountNodesToFeature(graphQuery: GraphQuery): Set<string> {
  switch (graphQuery.attributes.params.type) {
    case QueryType.NHopsFromAccount: {
      const accountIds: Set<string> = new Set();
      const params = graphQuery.attributes.params as NHopsFromAccountQuery;
      params.conditionals.forEach((conditional) => {
        // We just check for any conditional that check accounts
        if (conditional.left === ACCOUNT_DATUM_TYPE.name) {
          const value = conditional.right[0] as TextValue;
          if (value.variable) {
            const variable = params.variables.find((variable) => variable.name === value.name);
            if (variable) {
              accountIds.add(variable.value);
            }
          } else {
            accountIds.add(value.value);
          }
        }
      });
      return accountIds;
    }
    default:
      return new Set();
  }
}

async function lightlyHydrateQueryResults(
  nodeIds: NodeId[],
  { backend, graph, graphExplorer }: GraphQueryInfoContext,
): Promise<Map<HashableNodeId, LightlyHydratedNode>> {
  return await backend.lightlyHydrate({ nodeIds, graphId: graph.id }).then((response) => {
    const lightHydrationData: Map<HashableNodeId, LightlyHydratedNode> = new Map(
      nodeIds.map((nodeId, index) => {
        const lightlyHydratedNode = convertToLightlyHydratedNode(
          graphExplorer.getDatumTypeByToken(nodeId.token),
          response['lightly-hydrated-nodes'][index] as LightlyHydratedNode,
        );
        return [nodeIdToHashable(nodeId), lightlyHydratedNode];
      }),
    );
    return lightHydrationData;
  });
}

export async function generateGraphQueryInfo(
  graphQuery: GraphQuery,
  snapshot: GraphSnapshot | null,
  // it might be better just to pass in lightlyHydrateQueryResults and getDatumTypeByToken instead of the
  // graph explorer/backend context, but we can do that later
  graphQueryInfoContext: GraphQueryInfoContext,
): Promise<GraphQueryInfo | null> {
  const response = graphQuery.attributes.result;
  if (graphQuery.attributes['redacted-at'] || response == null) {
    return {
      v2Data: {
        edges: [],
        nodes: [],
        nonfetchedEdgeCounts: new Map(),
        seedNodes: [],
      },
      lightHydrationData: new Map(),
      hydrationData: new Map(),
      graphQuery: sanitizeGraphQuery(graphQuery),
    };
  }

  const { graphExplorer } = graphQueryInfoContext;
  const v2Data: V2Data = graphQueryResponseToV2Data(response);
  // since nodes are not expandable in GraphV2Card, we do not populate 'nonfetched-edge-count'
  // That way, visually, the purple dot indicating the icon is expandable will not be present
  v2Data.nonfetchedEdgeCounts = new Map();
  const { nodes } = v2Data;

  const hydrationData: Map<HashableNodeId, HydratedNode> = new Map();
  let lightHydrationData: Map<HashableNodeId, LightlyHydratedNode> = new Map();

  // extract nodes to feature
  const accountIdsToFeature = accountNodesToFeature(graphQuery);
  for (const node of nodes) {
    if (node.id.token === ACCOUNT_DATUM_TYPE.token && accountIdsToFeature.has(node.id.value)) {
      node.state.featured = true;
    }
  }

  const nodeIdsToLightlyHydrateSet: Set<NodeId> = new Set(nodes.map((node) => node.id));
  // if a snapshot exists, use the hydration results of all nodes that exists
  if (snapshot) {
    snapshot.attributes.nodes.forEach((node) => {
      const nodeId = snapshotNodeIdToGraphNodeId(node.id);
      if (node['hydration-values']) {
        const hydratedNode = convertToHydratedNode(
          graphExplorer.getDatumTypeByToken(nodeId.token),
          node['hydration-values'],
        );
        const hashableNodeId = nodeIdToHashable(nodeId);
        hydrationData.set(hashableNodeId, hydratedNode);
        lightHydrationData.set(
          hashableNodeId,
          convertHydratedNodeToLightlyHydratedNode(hydratedNode),
        );
        nodeIdsToLightlyHydrateSet.delete(nodeId);
      }
    });
  }

  if (nodeIdsToLightlyHydrateSet.size > 0) {
    const extraLightHydrationData = await lightlyHydrateQueryResults(
      Array.from(nodeIdsToLightlyHydrateSet),
      graphQueryInfoContext,
    );
    lightHydrationData = new Map([...lightHydrationData, ...extraLightHydrationData]);
  }

  return {
    v2Data,
    lightHydrationData,
    hydrationData,
    graphQuery: sanitizeGraphQuery(graphQuery),
  };
}

export type GenerateQueryGraphInfoFn = typeof generateGraphQueryInfo;
